import React, { useEffect, useRef } from 'react';
import { pointerEngine, PlayGesture } from './pointerEngine';
import { playLoop } from './playLoop';
import { useStorm } from '../storm/StormContext';
import { useFpsBackoff } from '../storm/useFpsBackoff';
import './play.css';

// One full-viewport canvas for every 2D burst: shockwave rings, touch ink trail, sparks.
// Fixed-size pools (no per-frame allocation); the loop only runs while something is alive.
const MAX_RINGS = 6;
const MAX_SPARKS = 64;
const MAX_TRAIL = 48;
const RING_MS = 600;
const RING_R = 220;
const SPARK_MS = 350;
const TRAIL_MS = 400;
const CREAM = '242,240,233';
const TWO_PI = Math.PI * 2;

type Ring = { on: boolean; x: number; y: number; rx: number; ry: number; rot: number; t0: number; k: number };
type Spark = { on: boolean; x: number; y: number; vx: number; vy: number; len: number; t0: number };
type Pt = { x: number; y: number; t: number };

const rings: Ring[] = Array.from({ length: MAX_RINGS }, () => ({ on: false, x: 0, y: 0, rx: 1, ry: 1, rot: 0, t0: 0, k: 1 }));
const sparks: Spark[] = Array.from({ length: MAX_SPARKS }, () => ({ on: false, x: 0, y: 0, vx: 0, vy: 0, len: 6, t0: 0 }));
const trail: Pt[] = Array.from({ length: MAX_TRAIL }, () => ({ x: 0, y: 0, t: -1e9 }));
let trailHead = 0;

let ctx: CanvasRenderingContext2D | null = null;
let cssW = 0;
let cssH = 0;
let offX = 0; // canvas rect origin in client px (gesture coords are clientX/Y)
let offY = 0;
let enabled = false; // canvas mounted and motion allowed
let ghosts = true; // backoff step 0 only
let inkOn = true; // backoff step 0 only
let fxOn = true; // backoff step < 2
let unregister: (() => void) | null = null;
let measure: (() => void) | null = null; // re-reads the canvas rect (set while mounted)

const nowMs = () => performance.now();

function anyAlive(t: number): boolean {
  for (let i = 0; i < MAX_RINGS; i++) if (rings[i].on) return true;
  for (let i = 0; i < MAX_SPARKS; i++) if (sparks[i].on) return true;
  for (let i = 0; i < MAX_TRAIL; i++) if (t - trail[i].t < TRAIL_MS) return true;
  return false;
}

// Re-read the rect when the loop is idle, before positions are offset: a 0/intrinsic rect
// captured at mount must never stick. (While the loop runs, resize listeners keep it fresh.)
function freshen() { if (!unregister && enabled) measure?.(); }

function ensureLoop() {
  if (unregister || !enabled) return;
  unregister = playLoop.register(tick);
}

function strokeRing(r: Ring, p: number, dx: number, color: string, alpha: number) {
  if (!ctx) return;
  ctx.strokeStyle = `rgba(${color},${alpha})`;
  ctx.beginPath();
  ctx.ellipse(r.x + dx, r.y, Math.max(0.5, r.rx * p), Math.max(0.5, r.ry * p), r.rot, 0, TWO_PI);
  ctx.stroke();
}

function drawRings(t: number) {
  if (!ctx) return;
  ctx.lineWidth = 2;
  for (let i = 0; i < MAX_RINGS; i++) {
    const r = rings[i];
    if (!r.on) continue;
    const p = (t - r.t0) / RING_MS;
    if (p >= 1) { r.on = false; continue; }
    const e = 1 - Math.pow(1 - Math.max(0, p), 3);
    const a = 0.55 * (1 - Math.max(0, p)) * r.k;
    if (ghosts) {
      ctx.lineWidth = 1;
      strokeRing(r, e, -1, '255,60,60', a * 0.7);
      strokeRing(r, e, 1, '60,230,255', a * 0.7);
      ctx.lineWidth = 2;
    }
    strokeRing(r, e, 0, CREAM, a);
  }
}

function drawSparks(t: number, dt: number) {
  if (!ctx) return;
  ctx.lineWidth = 1.5;
  const damp = Math.pow(0.002, dt); // strong per-second damping, framerate independent
  for (let i = 0; i < MAX_SPARKS; i++) {
    const s = sparks[i];
    if (!s.on) continue;
    const p = (t - s.t0) / SPARK_MS;
    if (p >= 1) { s.on = false; continue; }
    s.x += s.vx * dt; s.y += s.vy * dt;
    s.vx *= damp; s.vy *= damp;
    const sp = Math.hypot(s.vx, s.vy) || 1;
    ctx.strokeStyle = `rgba(${CREAM},${0.9 * (1 - p)})`;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(s.x - (s.vx / sp) * s.len, s.y - (s.vy / sp) * s.len);
    ctx.stroke();
  }
}

function drawTrail(t: number) {
  if (!ctx) return;
  // Oldest -> newest, segment width tapers 0 -> 6 with age.
  ctx.lineCap = 'round';
  ctx.strokeStyle = `rgba(${CREAM},0.5)`;
  let prev: Pt | null = null;
  for (let n = 1; n <= MAX_TRAIL; n++) {
    const pt = trail[(trailHead + n) % MAX_TRAIL];
    const age = t - pt.t;
    if (age >= TRAIL_MS) { prev = null; continue; }
    if (prev) {
      ctx.lineWidth = 6 * (1 - age / TRAIL_MS);
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
    }
    prev = pt;
  }
  ctx.lineCap = 'butt';
}

function tick(dt: number) {
  const g = ctx;
  if (!g) return;
  const t = nowMs();
  if (!anyAlive(t)) {
    g.clearRect(0, 0, cssW, cssH);
    if (unregister) { unregister(); unregister = null; }
    return;
  }
  g.clearRect(0, 0, cssW, cssH);
  drawTrail(t);
  drawRings(t);
  drawSparks(t, dt);
}

function ringSlot(): Ring {
  let oldest = rings[0];
  for (let i = 0; i < MAX_RINGS; i++) {
    if (!rings[i].on) return rings[i];
    if (rings[i].t0 < oldest.t0) oldest = rings[i];
  }
  return oldest;
}

export const fx = {
  ring(x: number, y: number, opts: { vx?: number; vy?: number; strength?: number } = {}) {
    if (!enabled || !fxOn) return;
    freshen();
    const r = ringSlot();
    const vx = opts.vx || 0, vy = opts.vy || 0;
    const speed = Math.hypot(vx, vy); // px/ms
    const ratio = speed > 0.05 ? 1 + Math.min(1.2, speed * 0.6) : 1;
    const k = Math.min(1, Math.max(0.2, opts.strength ?? 1));
    r.on = true; r.x = x - offX; r.y = y - offY; r.k = k;
    r.rx = RING_R * (0.6 + 0.4 * k); r.ry = r.rx / ratio;
    r.rot = ratio > 1 ? Math.atan2(vy, vx) : 0;
    r.t0 = nowMs();
    ensureLoop();
  },
  sparks(x: number, y: number, count: number, dirX: number, dirY: number) {
    if (!enabled || !fxOn) return;
    freshen();
    const n = Math.min(MAX_SPARKS, Math.max(0, Math.floor(count)));
    const base = Math.atan2(dirY, dirX);
    const t = nowMs();
    for (let k = 0; k < n; k++) {
      let s: Spark | null = null;
      for (let i = 0; i < MAX_SPARKS; i++) if (!sparks[i].on) { s = sparks[i]; break; }
      if (!s) return;
      const a = base + (Math.random() - 0.5) * 1.2;
      const sp = 120 + Math.random() * 220;
      s.on = true; s.x = x - offX; s.y = y - offY; s.vx = Math.cos(a) * sp; s.vy = Math.sin(a) * sp;
      s.len = 4 + Math.random() * 6; s.t0 = t;
    }
    ensureLoop();
  },
};

function pushTrail(x: number, y: number) {
  freshen();
  trailHead = (trailHead + 1) % MAX_TRAIL;
  const p = trail[trailHead];
  p.x = x - offX; p.y = y - offY; p.t = nowMs();
  ensureLoop();
}

const TEXT_SEL = 'textarea,input,[contenteditable]';
function tapAllowed(target: Element | null): boolean {
  if (!target) return true;
  // closest() only: a wrapper that merely contains a textarea still gets rings.
  return !(target.closest(TEXT_SEL) || target.closest('.gj-page-wrap canvas'));
}

function resetPools() {
  for (let i = 0; i < MAX_RINGS; i++) rings[i].on = false;
  for (let i = 0; i < MAX_SPARKS; i++) sparks[i].on = false;
  for (let i = 0; i < MAX_TRAIL; i++) trail[i].t = -1e9;
}

const EffectsCanvas: React.FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);
  const { reducedMotion } = useStorm();
  const q = useFpsBackoff();

  useEffect(() => {
    ghosts = q < 1; inkOn = q < 1; fxOn = q < 2;
    if (q >= 1 && ctx) { // quality stepped down: drop live trail points and wipe stale pixels
      for (let i = 0; i < MAX_TRAIL; i++) trail[i].t = -1e9;
      ctx.clearRect(0, 0, cssW, cssH);
    }
  }, [q]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || reducedMotion) return;
    const g = canvas.getContext('2d');
    if (!g) return;
    // Buffer follows the element's real CSS box (not innerWidth/100vh, which disagree with the
    // fixed box on mobile browsers whose toolbars never collapse). Drawing is in CSS px relative
    // to the canvas rect: fx.* take client coords and subtract rect.left/top.
    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      cssW = r.width || canvas.clientWidth; cssH = r.height || canvas.clientHeight;
      offX = r.left; offY = r.top;
      const bw = Math.max(1, Math.round(cssW * dpr)), bh = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    measure = size;
    ctx = g; enabled = true;
    const vv = window.visualViewport;
    window.addEventListener('resize', size);
    vv?.addEventListener('resize', size);
    const offs = [
      pointerEngine.on('tap', (e: PlayGesture) => { if (tapAllowed(e.target)) fx.ring(e.x, e.y); }),
      pointerEngine.on('flick', (e: PlayGesture) => { fx.ring(e.x, e.y, { vx: e.vx, vy: e.vy }); }),
      pointerEngine.on('move', (e: PlayGesture) => {
        if (inkOn && pointerEngine.state.down && e.type !== 'mouse') pushTrail(e.x, e.y);
      }),
    ];
    const w = window as unknown as { __playFx?: { alive: () => number; trailCount: () => number } };
    if (process.env.NODE_ENV !== 'production') {
      w.__playFx = {
        alive: () => rings.filter((r) => r.on).length,
        trailCount: () => { const t = nowMs(); return trail.filter((p) => t - p.t < TRAIL_MS).length; },
      };
    }
    return () => {
      offs.forEach((o) => o());
      window.removeEventListener('resize', size);
      vv?.removeEventListener('resize', size);
      enabled = false; ctx = null; measure = null;
      if (unregister) { unregister(); unregister = null; }
      resetPools();
      if (process.env.NODE_ENV !== 'production') delete w.__playFx;
    };
  }, [reducedMotion]);

  if (reducedMotion) return null;
  return <canvas ref={ref} className="play-fx" aria-hidden="true" />;
};

export default EffectsCanvas;
